import React, { useState } from "react";
import PageHeader from "../common/components/PageHeader";
import { Box, Grid, Link, Stack, Typography, useTheme } from "@mui/material";
import CommonCard from "../common/components/CommonCard";
import CommonFormField from "../common/components/CommonFormField";
import { ContactReasons, removeEmptyFields, validateEmail } from "../common/const";
import CommonSelect from "../common/components/CommonSelect";
import CommonButton from "../common/components/CommonButton";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../common/components/LoadingSpinner";
import { setLoading } from "../features/authSlice";
import { showNotification } from "../common/components/Notification/ToastifyNotification";
import { submitContactForm } from "../api/staticPageApi";

const ContactSupport = () => {
    const theme = useTheme();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        subject: '',
        message: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'firstname':
                if (!value) return 'First Name is required';
                if (!/^[A-Za-z ]+$/.test(value)) return 'First Name must contain only alphabetic characters';
                return '';
            case 'lastname':
                if (!value) return 'Last Name is required';
                if (!/^[A-Za-z ]+$/.test(value)) return 'Last Name must contain only alphabetic characters';
                return '';
            case 'email':
                if (!value) return 'Email is required';
                if (!validateEmail(value)) return 'Please enter a valid email address';
                return '';
            case 'subject':
                if (!value) return 'Subject is required';
                return '';
            case 'message':
                if (!value) return 'Message is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const data = {
                first_name: formData.firstname,
                last_name: formData.lastname,
                email: formData.email,
                subject: formData.subject,
                message: formData.message,
                student_id: user?.student_id,
                user_id: user?.parent_user_id || user?.user_id
            };
            const response = await submitContactForm(removeEmptyFields(data));
            if (response.data?.contact_us_id) {
                showNotification(response?.message, 'success');
                setFormData({
                    firstname: '',
                    lastname: '',
                    email: '',
                    subject: '',
                    message: ''
                });
                dispatch(setLoading({
                    loading: false
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err.response.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    };

    return (
        <Box sx={{ p: location.pathname === '/contact-us' ? 4 : 0 }}>
            {loading && (
                <LoadingSpinner />
            )}
            <PageHeader
                title="Contact Us"
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                            Have questions about the college application process? We're here to help!
                        </Typography>
                    </Stack>
                }
                color={theme.palette.secondary.main}
            />
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <CommonCard
                        content={
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <CommonFormField
                                            label="First Name *"
                                            type="text"
                                            name="firstname"
                                            value={formData.firstname}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter first name"
                                            error={errors.firstname}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <CommonFormField
                                            label="Last Name *"
                                            type="text"
                                            name="lastname"
                                            value={formData.lastname}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter last name"
                                            error={errors.lastname}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <CommonFormField
                                            label="Email *"
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter email id"
                                            error={errors.email}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <CommonSelect
                                            label="Subject *"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleInputChange}
                                            placeholder="Select Reason"
                                            options={ContactReasons}
                                            error={errors.subject}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <CommonFormField
                                            label="Message *"
                                            type="email"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder="What's on your mind?"
                                            error={errors.message}
                                            multiline
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <CommonButton
                                            label="Submit"
                                            size="small"
                                            height="40px"
                                            marginBottom="0px"
                                            fontWeight="bold"
                                            background={theme.palette.secondary.main}
                                            color={theme.palette.background.main}
                                            handleClick={handleSubmit}
                                        />
                                    </Grid>
                                </Grid>
                                <center>
                                    <Typography mt={2}>
                                        You can also reach us at
                                        <Link
                                            color="secondary.main"
                                            sx={{ textDecoration: 'none', ml: 0.5 }}
                                            href="mailto:support@mycolleges.com"
                                        >
                                            support@mycolleges.com
                                        </Link>
                                    </Typography>
                                </center>
                            </Box>
                        }
                        borderRadius={2}
                        background="transparent"
                        border={`1px solid ${theme.palette.background.secondary}`}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <CommonCard
                        content={
                            <Box
                                sx={{
                                    height: '500px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <img src="/Images/customersupport.png" alt="customer support" width="45%" />
                            </Box>
                        }
                        borderRadius={2}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}

export default ContactSupport;